import {
  applySalesCycleResponseSchema,
  salesCycleImpactResponseSchema,
  salesCycleListResponseSchema,
  salesCycleSchema,
  type ApplySalesCycleRequest,
  type ApplySalesCycleResponse,
  type CreateSalesCycleRequest,
  type SalesCycle,
  type SalesCycleImpactRequest,
  type SalesCycleImpactResponse,
} from '@complan/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/client';
import { queryKeys } from '@/shared/query/query-keys';
import { useInvalidateCalendar } from '@/features/calendar/use-calendar-freshness';

/**
 * The server reads and writes the Sales_Cycle_Timing_Form and its impact list
 * depend on (task 23.3), each a thin wrapper over an endpoint the task-16 API
 * already built. The impact computation and the reassignment are server-side
 * (`computeSalesCycleImpact` / `applySalesCycle`); nothing here re-derives them.
 *
 * The two-call date-change protocol is why `impact` and `apply` are separate
 * mutations: `impact` previews the affected Initiatives without persisting
 * anything (R6.7), and `apply` persists the definition and reassigns only the
 * selected Initiatives (R6.8, R6.9). A create or an edit that carries the
 * overlap/gap acknowledgement is the `useSaveSalesCycle` mutation; the form
 * resubmits it with the acknowledged rule ids once the user confirms.
 *
 * Every wire body is parsed through the contracts schema, so a drifted shape
 * fails at the boundary rather than mis-rendering, and every call goes through
 * the shared `apiFetch`, so a non-2xx surfaces as a typed `ApiRequestError` whose
 * `body` the form classifies (`sales-cycle-model.ts`).
 */

/** The active Sales_Cycle definitions of one planning year, in date order (R6.1). */
export async function fetchSalesCyclesForYear(
  planningYear: number,
  includeInactive: boolean,
): Promise<readonly SalesCycle[]> {
  const params = new URLSearchParams({ planningYear: String(planningYear) });
  if (includeInactive) {
    params.set('includeInactive', 'true');
  }
  const body = await apiFetch<unknown>(`/api/sales-cycles?${params.toString()}`);
  return salesCycleListResponseSchema.parse(body).items;
}

/** React Query hook for the year's Sales_Cycle definitions (including inactive for the editor). */
export function useSalesCycleDefinitions(
  planningYear: number,
): UseQueryResult<readonly SalesCycle[], unknown> {
  return useQuery({
    queryKey: [...queryKeys.salesCycles(planningYear), 'all'],
    queryFn: () => fetchSalesCyclesForYear(planningYear, true),
  });
}

/** A create (`POST`) or edit (`PUT`) of a definition, with the warnings acknowledged (R6.4, R6.5). */
export interface SaveSalesCycleInput {
  /** Present for an edit; absent for a create. */
  readonly id?: string;
  readonly request: CreateSalesCycleRequest;
}

/**
 * Creates or edits a Sales_Cycle definition (R6.1–R6.5).
 *
 * A create is `POST /api/sales-cycles`; an edit is `PUT /api/sales-cycles/:id`.
 * Both take the definition plus `acknowledgedWarningRuleIds`; on success the
 * year's definitions are invalidated so the "sales cycles this year" list
 * refreshes. A failure throws `ApiRequestError`, which the form classifies into a
 * block (retain values) or an acknowledgement (resubmit with the rule ids).
 */
export function useSaveSalesCycle(
  planningYear: number,
): UseMutationResult<SalesCycle, unknown, SaveSalesCycleInput> {
  const queryClient = useQueryClient();
  const invalidateCalendar = useInvalidateCalendar();
  return useMutation({
    mutationFn: async ({ id, request }: SaveSalesCycleInput) => {
      const path = id === undefined ? '/api/sales-cycles' : `/api/sales-cycles/${id}`;
      const method = id === undefined ? 'POST' : 'PUT';
      const body = await apiFetch<unknown>(path, {
        method,
        body: JSON.stringify(request),
      });
      return salesCycleSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.salesCycles(planningYear) });
      // A Sales_Cycle definition change moves the Sales_Cycle-orientation bands and
      // can re-derive the assigned cycle set the bars carry, so the acting user's
      // calendar must refresh within 3 s too (R13.3, task 22.7).
      invalidateCalendar();
    },
  });
}

/** The impact preview of a proposed date change on an existing definition (R6.7). */
export interface ImpactInput {
  readonly id: string;
  readonly request: SalesCycleImpactRequest;
}

/** Computes the impact list of a proposed date change (R6.7); persists nothing. */
export function useSalesCycleImpact(): UseMutationResult<
  SalesCycleImpactResponse,
  unknown,
  ImpactInput
> {
  return useMutation({
    mutationFn: async ({ id, request }: ImpactInput) => {
      const body = await apiFetch<unknown>(`/api/sales-cycles/${id}/impact`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return salesCycleImpactResponseSchema.parse(body);
    },
  });
}

/** Applies a date change and reassigns the selected Initiatives (R6.8, R6.9). */
export interface ApplyInput {
  readonly id: string;
  readonly request: ApplySalesCycleRequest;
}

/**
 * Persists a Sales_Cycle date change and reassigns only the selected Initiatives
 * (R6.8–R6.11). On success the year's definitions are invalidated. A stale-preview
 * `409` surfaces as an `ApiRequestError` the impact screen turns into a
 * re-preview prompt.
 */
export function useApplySalesCycle(
  planningYear: number,
): UseMutationResult<ApplySalesCycleResponse, unknown, ApplyInput> {
  const queryClient = useQueryClient();
  const invalidateCalendar = useInvalidateCalendar();
  return useMutation({
    mutationFn: async ({ id, request }: ApplyInput) => {
      const body = await apiFetch<unknown>(`/api/sales-cycles/${id}/apply`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
      return applySalesCycleResponseSchema.parse(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.salesCycles(planningYear) });
      // Applying a date change reassigns Initiatives, moving their bars and cycle
      // bands, so the acting user's calendar re-renders within 3 s (R13.3).
      invalidateCalendar();
    },
  });
}
